import Icons from '../../../Icons'
import { useEffect, useState } from 'react'
import SliderCustom from '@/components/SliderCustom'
import ColorsPackContainer from '@/ColorsPackContainer'
import { useTranslation } from 'react-i18next'
import Loading from '@/components/Loading'
import { lightTheme } from '@/customTheme'

function Shadow({applyEffectLayerShadow,
  updateOptions,
  updateEffect,
  calcMaxForKey,
  calcMinForKey,
  defaultForKey,
  target,
  waitUntilReady,
  canReset = false,
  onResetStateChange,
}: {
  applyEffectLayerShadow: (isReset: boolean, shadow: number, distance: number, angle: number, color: string, blur: number) => Promise<void>
  updateOptions: (obj: any) => any
  updateEffect: (final?: boolean) => void
  calcMaxForKey: (key: string) => number
  calcMinForKey: (key: string) => number
  defaultForKey: (key: string) => any
  target: any
  waitUntilReady: (onReady: () => void, signal?: AbortSignal) => void
  /** When true, the Reset button fires even if displayed slider values are already at minimum. */
  canReset?: boolean
  /** Reports the current reset handler + whether it's actionable, so a host (e.g. the inspector header) can render its own Reset button. */
  onResetStateChange?: (reset: () => void, isResetEnabled: boolean) => void
}) {

  const [distanceValue, setDistanceValue] = useState(calcMinForKey('distance'));
  const [angleValue, setAngleValue] = useState(calcMinForKey('angle'));
  const [blurValue, setBlurValue] = useState(calcMinForKey('blur'));
  const [shadowValue, setShadowValue] = useState(calcMinForKey('opacity'));
  const [colorValue, setColorValue] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false)

  const [didChangeShadow, setDidChangeShadow] = useState(false)
  const [didChangeDistance, setDidChangeDistance] = useState(false)
  const [didChangeAngle, setDidChangeAngle] = useState(false)
  const [didChangeBlur, setDidChangeBlur] = useState(false)

  const handleObjectShadow = () => {
    let {opacity, distance, angle, blur, color, promise} = updateOptions(target);
    
    setShadowValue(opacity ?? calcMinForKey('opacity'))
    setDistanceValue(distance ?? calcMinForKey('distance'))
    setAngleValue(angle ?? calcMinForKey('angle'))
    setBlurValue(blur ?? calcMinForKey('blur'))
    setColorValue(color ?? null)
    setDidChangeShadow(false)
    setDidChangeDistance(false)
    setDidChangeAngle(false)
    setDidChangeBlur(false)
    return promise
  }

  useEffect(() => {
    if(!target) return
    // Invalidate immediately on target switch — before this, isInitialized/values
    // still describe the previously selected target.
    setIsInitialized(false)
    const abortController = new AbortController()
    waitUntilReady(() => {
      if (abortController.signal.aborted) return
      setIsInitialized(true)
      handleObjectShadow()
    }, abortController.signal)

    return () => {
      abortController.abort()
    }
  }, [target])
  
  const reset = () => {
    const minOpacity = calcMinForKey("opacity")
    const minDistance = calcMinForKey("distance")
    const minAngle = calcMinForKey("angle")
    const minBlur = calcMinForKey("blur")
    const alreadyAtMin = shadowValue === minOpacity && distanceValue === minDistance && angleValue === minAngle && blurValue === minBlur && colorValue === null
    if(alreadyAtMin && !canReset) {
      return
    }
    setShadowValue(minOpacity)
    setDistanceValue(minDistance)
    setAngleValue(minAngle)
    setBlurValue(minBlur)
    setColorValue(null)
    setDidChangeShadow(false)
    setDidChangeDistance(false)
    setDidChangeAngle(false)
    setDidChangeBlur(false)
    applyEffectShadow(minOpacity, minDistance, minAngle, minBlur, null, true)
  }

  useEffect(() => {
    if (!isInitialized) {
      // Target is (re)initializing — invalidate any previously reported handler so
      // the header button can't fire against the target we're switching away from.
      onResetStateChange?.(() => {}, false)
      return
    }
    const minOpacity = calcMinForKey("opacity")
    const minDistance = calcMinForKey("distance")
    const minAngle = calcMinForKey("angle")
    const minBlur = calcMinForKey("blur")
    const alreadyAtMin = shadowValue === minOpacity && distanceValue === minDistance && angleValue === minAngle && blurValue === minBlur && colorValue === null
    // canReset also conveys "dirty" for batch (mixed-selection sliders can show
    // at-min while some images actually carry a shadow) — see ShadowBatch.
    onResetStateChange?.(reset, canReset || !alreadyAtMin)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, isInitialized, shadowValue, distanceValue, angleValue, blurValue, colorValue, canReset])

  const applyEffectShadow = async (nextShadow?: number, nextDistance?: number, nextAngle?: number, nextBlur?: number, nextColor?: string, isReset: boolean = false) => {
    await applyEffectLayerShadow(isReset, nextShadow ?? shadowValue, nextDistance ?? distanceValue, nextAngle ?? angleValue, nextColor ?? colorValue, nextBlur ?? blurValue)
    updateEffect(isReset)
  }

  const updateUI = (args: { nextShadow?: number, nextDistance?: number, nextAngle?: number, nextBlur?: number, nextColor?: string }) => {
    const hasNextShadow = args.nextShadow != null;
    const hasNextDistance = args.nextDistance != null;
    const hasNextAngle = args.nextAngle != null;
    const hasNextBlur = args.nextBlur != null;
    const hasNextColor = args.nextColor != null;

    let resolvedShadow = hasNextShadow ? args.nextShadow : shadowValue
    let resolvedDistance = hasNextDistance ? args.nextDistance : distanceValue
    let resolvedAngle = hasNextAngle ? args.nextAngle : angleValue
    let resolvedBlur = hasNextBlur ? args.nextBlur : blurValue
    let resolvedColor = hasNextColor ? args.nextColor : colorValue
    
    const minShadow = calcMinForKey("opacity")
    const minDistance = calcMinForKey("distance")
    const minAngle = calcMinForKey("angle")
    const minBlur = calcMinForKey("blur")

    const defaultShadow = defaultForKey('opacity')
    const defaultDistance = defaultForKey('distance')
    const defaultAngle = defaultForKey('angle')
    const defaultBlur = defaultForKey('blur')
    const defaultColor = defaultForKey('color')

    const autoApplyDefaults = (changed: "shadow" | "distance" | "angle" | "blur") => {
      if (resolvedColor !== null) return;
      const autoApply =
        (changed === "shadow" || (resolvedShadow === minShadow && !didChangeShadow)) &&
        (changed === "distance" || (resolvedDistance === minDistance && !didChangeDistance)) &&
        (changed === "angle" || (resolvedAngle === minAngle && !didChangeAngle)) &&
        (changed === "blur" || (resolvedBlur === minBlur && !didChangeBlur));

      if (!autoApply) return;
      setColorValue(defaultColor);
      resolvedColor = defaultColor;
      if (changed !== "shadow") {
        setShadowValue(defaultShadow);
        resolvedShadow = defaultShadow;
      }
      if (changed !== "distance") {
        setDistanceValue(defaultDistance);
        resolvedDistance = defaultDistance;
      }
      if (changed !== "angle") {
        setAngleValue(defaultAngle);
        resolvedAngle = defaultAngle;
      }
      if (changed !== "blur") {
        setBlurValue(defaultBlur);
        resolvedBlur = defaultBlur;
      }
    }

    if (hasNextShadow) {
      setShadowValue(args.nextShadow as number)
      resolvedShadow = args.nextShadow as number
      setDidChangeShadow(true)
      autoApplyDefaults("shadow");
    }
    if (hasNextDistance) {
      setDistanceValue(args.nextDistance as number)
      resolvedDistance = args.nextDistance as number
      setDidChangeDistance(true)
      autoApplyDefaults("distance");
    }
    if (hasNextAngle) {
      setAngleValue(args.nextAngle as number)
      resolvedAngle = args.nextAngle as number
      setDidChangeAngle(true)
      autoApplyDefaults("angle");
    }
    if (hasNextBlur) {
      setBlurValue(args.nextBlur as number)
      resolvedBlur = args.nextBlur as number
      setDidChangeBlur(true)
      autoApplyDefaults("blur");
    }


    if (hasNextColor) {
      const wasColorNull = colorValue === null
      setColorValue(args.nextColor as string)
      resolvedColor = args.nextColor as string
      if(wasColorNull) {
        if (resolvedShadow === minShadow && !didChangeShadow) {
          setShadowValue(defaultShadow)
          resolvedShadow = defaultShadow
        }
        if (resolvedDistance === minDistance && !didChangeDistance) {
          setDistanceValue(defaultDistance)
          resolvedDistance = defaultDistance
        }
        if (resolvedAngle === minAngle && !didChangeAngle) {
          setAngleValue(defaultAngle)
          resolvedAngle = defaultAngle
        }
        if (resolvedBlur === minBlur && !didChangeBlur) {
          setBlurValue(defaultBlur)
          resolvedBlur = defaultBlur
        }
      }
    }
    applyEffectShadow(resolvedShadow as number, resolvedDistance as number, resolvedAngle as number, resolvedBlur as number, resolvedColor as string)
  }

  const onShadowChange = (uiValue, logicValue) => updateUI({ nextShadow: logicValue })
  const onDistanceChange = (uiValue, logicValue) => updateUI({ nextDistance: logicValue })
  const onAngleChange = (uiValue, logicValue) => updateUI({ nextAngle: logicValue })
  const onBlurChange = (uiValue, logicValue) => updateUI({ nextBlur: logicValue })
  const onColorChange = (color) => updateUI({ nextColor: color })
  
  const { t } = useTranslation()

  return (
    <>
    {isInitialized ? (
      <>
          {/* Shadow */}
          <SliderCustom
            icon={<Icons.Shadow size={24} />}
            name={t("Shadow")}
            minValue={0}
            maxValue={100}
            logicMaximum={calcMaxForKey("opacity")}
            logicMinimum={calcMinForKey("opacity")}
            listValues={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
            onChangeValue={onShadowChange}
            value={shadowValue}
            onFinalChangeValue={(value, logicalValue) => {
              updateEffect(true)
            }}
            style={{ color: lightTheme.colors.blackGray, marginTop: 0 }}
          />
          {/* Distance */}
          <SliderCustom
            icon={<Icons.Distance size={24} />}
            name={t("Distance")}
            minValue={0}
            maxValue={100}
            logicMaximum={calcMaxForKey("distance")}
            logicMinimum={calcMinForKey("distance")}
            listValues={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
            onChangeValue={onDistanceChange}
            value={distanceValue}
            onFinalChangeValue={(value, logicalValue) => {
              updateEffect(true)
            }}
            style={{ color: lightTheme.colors.blackGray }}
          />
          {/* Angle */}
          <SliderCustom
            icon={<Icons.Angle size={24} />}
            name={t("Angle")}
            minValue={0}
            maxValue={360}
            logicMaximum={calcMaxForKey("angle")}
            logicMinimum={calcMinForKey("angle")}
            listValues={[
              0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160, 170, 180, 190, 200,
              210, 220, 230, 240, 250, 260, 270, 280, 290, 300, 310, 320, 330, 340, 350, 360,
            ]}
            onChangeValue={onAngleChange}
            value={angleValue}
            onFinalChangeValue={(value, logicalValue) => {
              updateEffect(true)
            }}
            style={{ color: lightTheme.colors.blackGray }}
          />
          {/* Blur */}
          <SliderCustom
            icon={<Icons.Blur size={24} />}
            name={t("Blur")}
            minValue={0}
            maxValue={100}
            logicMaximum={calcMaxForKey("blur")}
            logicMinimum={calcMinForKey("blur")}
            listValues={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
            onChangeValue={onBlurChange}
            value={blurValue}
            onFinalChangeValue={(value, logicalValue) => {
              updateEffect(true)
            }}
            style={{ color: lightTheme.colors.blackGray }}
          />
          <ColorsPackContainer
            style={{
              boxSizing: 'border-box',
              padding: '0',
              width: '300px',
              paddingRight: '8px',
            }}
            onItemSelected={onColorChange}
            onFinalItemSelected={() => updateEffect(true)}
            colorSelected={colorValue}
            popoverPlacement='top-right'
            popoverOffset={{x: 0, y: 8}}
          ></ColorsPackContainer>
          </>
    ): (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Loading />
      </div>
    )}
    </>
  )
}

export default Shadow